import React from 'react'
import { Typography } from 'antd'
import RoleChips from 'enrollment-platform/pages/AccessControl/components/RoleChips/RoleChips'
import { useUserQuery } from 'enrollment-platform/pages/AccessControl/hooks/useUserQuery'
import { usePermission } from 'enrollment-platform/rbac/usePermission'

const { Text } = Typography

interface Props {
  /** The person's id, which is also their Cognito username. */
  cognitoUserId: string
}

/**
 * A status line in the chips' place. antd's Text is inline, so on its own it
 * would run on from the Roles label; the block keeps it on the next line.
 */
function Status({ children }: { children: string }) {
  return (
    <div>
      <Text type="secondary">{children}</Text>
    </div>
  )
}

/**
 * The person's RBAC roles as the Permission Management list shows them:
 * granted roles as chips, the implied role as the fallback when none are
 * granted. Read-only; roles are granted from Permission Management. The
 * lookup needs Read on UserPermission, so a viewer without it sees that the
 * roles are not available to them rather than an error. Every state renders
 * on its own line under the Roles label.
 */
export default function PersonRbacRoles({ cognitoUserId }: Props) {
  const { allowed, isLoading: isAbilityLoading } = usePermission(
    'Read',
    'UserPermission'
  )
  const { data, isLoading, isError } = useUserQuery(cognitoUserId, allowed)
  if (isAbilityLoading || (allowed && isLoading)) {
    return <Status>Loading…</Status>
  }
  if (!allowed) return <Status>Not available</Status>
  if (isError || !data) return <Status>Unavailable</Status>
  const roles = data.roles ?? data.roleIds.map((id) => ({ id, name: id }))
  return (
    <div>
      <RoleChips roles={roles} impliedRole={data.impliedRole} size="regular" />
    </div>
  )
}
